/**
 * Session Persistence
 * 
 * Saves the current game session to localStorage so the player can
 * return to game.selna.ai and continue without needing the magic link email.
 * 
 * Stored data:
 * - token: the JWT magic link token (used to navigate back to the correct page)
 * - adventureTitle: for display on the landing page
 * - currentEpisode: for display context
 * - totalEpisodes: for display context
 * - lastPage: which page the player was on ('play' | 'waiting' | 'complete')
 * - savedAt: ISO timestamp of when this was saved
 */

const STORAGE_KEY = 'selna_game_session';

// ============================================================================
// Episode Save State (mid-episode save for later resumption)
// ============================================================================

export interface EpisodeSaveState {
  // Identification
  odysseyId: string;
  episodeNumber: number;
  scenarioId: string | null;
  
  // Conversation
  transcript: Array<{
    timestamp: number;
    speaker: 'user' | 'ai';
    text: string;
    id?: string;
  }>;
  
  // Session state
  emotionHistory: Array<{
    timestamp: number;
    emotion: string;
  }>;
  duration: number;        // Elapsed time in ms
  turnCount: number;       // Number of user turns
  
  // Scenario context (for reconstruction)
  scenarioData: {
    title: string;
    aiRole: string;
    userRole: string;
    characterName?: string;
    prompt?: string;
    voice?: string;
  };
  
  // Metadata
  savedAt: string;         // ISO timestamp
  version: number;         // For future migration
}

export interface SavedSession {
  token: string;
  adventureTitle: string;
  /** Adventure id this session is bound to. Used by the catalog to detect
   *  when the player is trying to switch adventures and show a warning. */
  adventureId?: string;
  currentEpisode: number;
  totalEpisodes: number;
  lastPage: 'play' | 'waiting' | 'complete';
  playerStatus: 'active' | 'completed' | 'abandoned';
  savedAt: string;
}

/**
 * Save the current session to localStorage.
 */
export function saveSession(session: Omit<SavedSession, 'savedAt'>): void {
  try {
    const data: SavedSession = {
      ...session,
      savedAt: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (err) {
    // localStorage might be full or disabled — silently ignore
    console.warn('[SessionPersistence] Failed to save session:', err);
  }
}

/**
 * Load the saved session from localStorage.
 * Returns null if no session exists or data is corrupted.
 */
export function loadSession(): SavedSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const data = JSON.parse(raw);

    // Validate required fields exist
    if (!data.token || typeof data.token !== 'string') return null;
    if (!data.adventureTitle || typeof data.adventureTitle !== 'string') return null;
    if (typeof data.currentEpisode !== 'number') return null;
    if (typeof data.totalEpisodes !== 'number') return null;
    if (!data.lastPage || typeof data.lastPage !== 'string') return null;
    if (!data.savedAt || typeof data.savedAt !== 'string') return null;

    return data as SavedSession;
  } catch (err) {
    // Corrupted data — clear it
    console.warn('[SessionPersistence] Failed to load session, clearing:', err);
    clearSession();
    return null;
  }
}

/**
 * Clear the saved session from localStorage.
 */
export function clearSession(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (err) {
    console.warn('[SessionPersistence] Failed to clear session:', err);
  }
}

/**
 * Update only specific fields of the saved session (e.g. when navigating between pages).
 */
export function updateSession(updates: Partial<Omit<SavedSession, 'savedAt'>>): void {
  const current = loadSession();
  if (!current) return;

  saveSession({
    token: updates.token ?? current.token,
    adventureTitle: updates.adventureTitle ?? current.adventureTitle,
    currentEpisode: updates.currentEpisode ?? current.currentEpisode,
    totalEpisodes: updates.totalEpisodes ?? current.totalEpisodes,
    lastPage: updates.lastPage ?? current.lastPage,
    playerStatus: updates.playerStatus ?? current.playerStatus,
  });
}

// ============================================================================
// Player Email Persistence (for session revalidation on page load)
// ============================================================================

const EMAIL_KEY = 'selna_player_email';

export function savePlayerEmail(email: string): void {
  try {
    localStorage.setItem(EMAIL_KEY, email.toLowerCase().trim());
  } catch (err) {
    console.warn('[SessionPersistence] Failed to save email:', err);
  }
}

export function loadPlayerEmail(): string | null {
  try {
    return localStorage.getItem(EMAIL_KEY);
  } catch (err) {
    return null;
  }
}

export function clearPlayerEmail(): void {
  try {
    localStorage.removeItem(EMAIL_KEY);
  } catch (err) {
    console.warn('[SessionPersistence] Failed to clear email:', err);
  }
}
